import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowRight, AlertTriangle, CheckCircle2, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";

const SIGNAL_ICONS = { BUY: "↗", WATCH: "👁", HOLD: "⏸", NO_DATA: "•" };

const stripBullet = (s) => (s || "").replace(/^[\s✅⚠•·\-–—]+/, "").trim();

/**
 * SignalRationaleTooltip — Phase 7 Stage 6
 *
 * Wraps a signal badge / clickable target. On open, fetches the compact
 * cached rationale from /rationale/{symbol}/compact (never triggers a
 * Claude call). Shows top 3 supporting reasons + top 2 concerns + link
 * to the full Stock Detail rationale panel.
 *
 * Props:
 *   children     — the trigger element (e.g. the SignalBadge)
 *   cseSymbol    — required
 *   companyCode  — for the "View full analysis" deep link
 *   companyName  — bold header text
 *   signal       — current signal (kept in sync until cache refreshes)
 *   score        — current composite score
 */
const SignalRationaleTooltip = ({
  children,
  cseSymbol,
  companyCode,
  companyName,
  signal,
  score,
}) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onOpenChange = async (next) => {
    setOpen(next);
    if (next && !data && !loading) {
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet(`/rationale/${cseSymbol}/compact`);
        setData(res);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load rationale");
      } finally {
        setLoading(false);
      }
    }
  };

  const detailLink = companyCode ? `/stocks/${companyCode}` : null;
  const canGenerate = signal && signal !== "NO_DATA";

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {/* The trigger is whatever children pass — usually a signal badge.
            Stop event from bubbling so the row click doesn't navigate. */}
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
          aria-label={`Show rationale for ${cseSymbol}`}
          data-testid={`signal-tooltip-trigger-${cseSymbol}`}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        className="w-[300px] p-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid={`signal-tooltip-${cseSymbol}`}
      >
        <div className="px-3 pt-3 pb-2 border-b bg-muted/20">
          <div className="text-sm font-semibold leading-tight">{companyName || cseSymbol}</div>
          <div className="mt-1 flex items-center gap-2 text-[11px]">
            <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
              {SIGNAL_ICONS[signal] || "•"} {signal || "NO_DATA"}
            </Badge>
            {score != null && <span className="text-muted-foreground">Score {score}/100</span>}
          </div>
        </div>

        <div className="p-3 text-sm">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading…
            </div>
          )}

          {!loading && error && (
            <div className="text-xs text-warning">{error}</div>
          )}

          {!loading && data && data.available === false && (
            <div className="space-y-2 text-xs">
              {canGenerate ? (
                <>
                  <p className="text-muted-foreground">Analysis not yet generated.</p>
                  {detailLink && (
                    <Link
                      to={`${detailLink}?generate=1`}
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      Generate analysis <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <p className="text-muted-foreground">Insufficient data for analysis.</p>
                  <Link
                    to="/admin/reports"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <FileText className="h-3 w-3" />
                    Auto-download reports
                  </Link>
                </>
              )}
            </div>
          )}

          {!loading && data?.available && (
            <>
              {(data.why_signal_top || []).length > 0 && (
                <ul className="space-y-1 mb-2">
                  {data.why_signal_top.map((p, i) => (
                    <li key={i} className="flex gap-1.5 text-[12.5px] leading-snug">
                      <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0 text-[hsl(var(--success))]" />
                      <span>{stripBullet(p)}</span>
                    </li>
                  ))}
                </ul>
              )}

              {(data.concerns_top || []).length > 0 && (
                <>
                  <div className="my-2 border-t" />
                  <ul className="space-y-1">
                    {data.concerns_top.map((p, i) => (
                      <li key={i} className="flex gap-1.5 text-[12.5px] leading-snug">
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-warning" />
                        <span>{stripBullet(p)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>

        {detailLink && (
          <div className="border-t px-3 py-2 bg-muted/20">
            <Link
              to={detailLink}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              data-testid={`signal-tooltip-link-${cseSymbol}`}
            >
              View full analysis
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default SignalRationaleTooltip;
